import React, { useEffect, useState } from 'react';
import FormControl from '@mui/material/FormControl';
import { InputLabel } from '@mui/material';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import { LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs';
import { DateRangePicker } from '@mui/x-date-pickers-pro/DateRangePicker';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { locationMap } from '../transactions-page-helpers';
import { TransactionsDateFiltersParams } from '../transactions-page-params';
import { StatementPeriod } from '../transactions-page-types';

/**
 * We use utc/timezone functionality to set a static timezone for statement periods, etc.
 */
dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * Date filters for Transactions Datagrid.
 * @param dateRange the current date range of transactions
 * @param selectedStatementPeriod the current selected statement period
 * @param updateSelectedStatementPeriod setter for statement period
 * @param updateDateRange setter for date range
 * @param location basically transaction category (e.g. divvy or ramp)
 */
// eslint-disable-next-line import/prefer-default-export
export const TransactionsDateFilters = function ({
  dateRange,
  selectedStatementPeriod,
  updateSelectedStatementPeriod,
  updateDateRange,
  location,
}: TransactionsDateFiltersParams) {
  const [statementPeriods, setStatementPeriods] = useState<StatementPeriod[]>([]);

  // calculates statement periods on component load
  useEffect(() => {
    const periods = locationMap[location].getStatementPeriods();
    setStatementPeriods(periods);
    // we want to default the selected statement period to the most recent (periods are descending)
    updateSelectedStatementPeriod(periods[0]);
  }, []);

  if (!selectedStatementPeriod) return null;

  const [periodStart, periodEnd] = selectedStatementPeriod.period;

  return (
    <>
      {/*
        Statement period selector.
        Select component doesn't natively accept number[] as value, so we have to do some workarounds to convert and de-convert to/from string.
       */}
      <FormControl fullWidth className="mt-auto w-64">
        <InputLabel>Statement Period</InputLabel>
        <Select
          value={selectedStatementPeriod.label}
          onChange={(e) => {
            //   we can use label to identify period as no two labels should ever be remotely the same
            const relevantPeriod = statementPeriods.find((period) => period.label === e.target.value);
            if (!relevantPeriod) return;
            updateSelectedStatementPeriod(relevantPeriod);
          }}
          label="Statement Period"
        >
          {statementPeriods.map((period) => (<MenuItem value={period.label}>{period.label}</MenuItem>))}
        </Select>
      </FormControl>
      {/* Fine-tune date range selector (allows user to select sub range within period) */}
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DateRangePicker
          value={dateRange}
          onChange={(range) => { updateDateRange(range); }}
          className="mt-10 w-1/3"
          minDate={periodStart}
          maxDate={periodEnd}
        />
      </LocalizationProvider>
    </>
  );
};
